import React, { SyntheticEvent } from 'react';
import { Resizable, ResizableProps, ResizeCallbackData } from 'react-resizable';
import ReactDragListView from 'react-drag-listview';
import { Table as AntTable } from 'antd';
import { IDraggableTable, ITableColumn } from './types';
import styles from './Table.Draggable.module.scss';

const { useState, useCallback, useEffect, useMemo } = React;

const DEFAULT_COLUMN_MIN_WIDTH = 100;
const DEFAULT_COLUMN_MAX_WIDTH = 800;
const TABLE_HEADER_HEIGHT = 111;

const ResizableColumn = (props: ResizableProps) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={<span className="react-resizable-handle" />}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const DraggableTable = <RecordType extends object>(
  props: IDraggableTable<RecordType>
) => {
  const {
    onColumnDragEnd,
    columns: initialColumns,
    onColumnResize,
    selectable,
    onRowSelectChange,
    containerHeight,
    selectedKeys,
    ...restProps
  } = props;

  const [selected, setSelected] = useState<React.Key[]>([]);

  const [scrollX, setScrollX] = useState<number>(0);
  const [columns, setColumns] = useState<ITableColumn<RecordType>>(
    initialColumns || []
  );

  useEffect(() => {
    if (!Array.isArray(selectedKeys)) return;
    setSelected(selectedKeys);
  }, [selectedKeys]);

  const containerHeightVal: number = useMemo(() => {
    if (
      !containerHeight ||
      Number.isNaN(containerHeight || Number.NaN) ||
      (containerHeight || Number.NaN) < TABLE_HEADER_HEIGHT
    ) {
      return -1;
    }
    return containerHeight;
  }, [containerHeight]);

  const scrollVal = useMemo(() => {
    const scroll: { x?: number; y?: number } = {};
    if (scrollX > 0) {
      scroll.x = scrollX;
    }
    if (containerHeightVal > 0) {
      scroll.y = containerHeightVal - TABLE_HEADER_HEIGHT;
    }
    return scroll;
  }, [scrollX, containerHeightVal]);

  useEffect(() => {
    if (containerHeightVal > 0) {
      const tableBodyClassName = 'ant-table-body';
      const selector = document.getElementsByClassName(tableBodyClassName);
      const tableBodyContainer = selector[0] as HTMLElement;
      if (tableBodyContainer) {
        tableBodyContainer.removeAttribute('style');
        tableBodyContainer.style.overflow = 'auto';
        const adjustedHeight = containerHeightVal - TABLE_HEADER_HEIGHT;
        tableBodyContainer.style.height = `${adjustedHeight}px`;
      }
    }
  }, [containerHeightVal]);

  useEffect(() => {
    const fixedColumnsHandlers: { [key: string]: () => void } = {};
    const fixedColumnsSelector = [
      'th.ant-table-cell-fix-left',
      'th.ant-table-cell-fix-right',
    ];
    const fixedColumns = Array.from(
      document.querySelectorAll(fixedColumnsSelector.join(', '))
    );
    fixedColumns.forEach((col, i) => {
      const handler = () => {
        col.removeAttribute('draggable');
      };
      fixedColumnsHandlers[col.nodeName + i] = handler;
      col.addEventListener('mousedown', handler);
    });

    const allColumnsHandlers: { [key: string]: () => void } = {};
    const allColumnsSelector = 'th.ant-table-cell';
    const allColumns = Array.from(
      document.querySelectorAll(allColumnsSelector)
    );
    allColumns.forEach((col, i) => {
      const handler = () => {
        col.removeAttribute('draggable');
      };
      allColumnsHandlers[col.nodeName + i] = handler;
      col.addEventListener('click', handler);
    });

    return () => {
      fixedColumns.forEach((col, i) => {
        const handler = fixedColumnsHandlers[col.nodeName + i];
        col.removeEventListener('mousedown', handler);
      });
      allColumns.forEach((col, i) => {
        const handler = allColumnsHandlers[col.nodeName + i];
        col.removeEventListener('click', handler);
      });
    };
  }, []);

  useEffect(() => {
    const containerClass = 'ant-table-container';
    const conainer = document.getElementsByClassName(containerClass)[0];
    const containerWidth = (conainer as HTMLDivElement).offsetWidth;
    const columnsWidth = columns.reduce(
      (acc, col) => acc + Number(col.width || 0),
      0
    );
    if (columnsWidth > containerWidth) {
      setScrollX(columnsWidth);
    } else {
      setScrollX(0);
    }
  }, [setScrollX, columns]);

  const handleColumnDragEnd = useCallback(
    (fromIndex: number, toIndex: number) => {
      let from = fromIndex;
      let to = toIndex;
      if (selectable) {
        from -= 1;
        to -= 1;
      }

      if (columns[from].fixed || columns[to].fixed) {
        return;
      }

      const cols = [...columns];
      const item = cols.splice(from, 1)[0];
      cols.splice(to, 0, item);
      setColumns(cols);
      if (onColumnDragEnd) {
        onColumnDragEnd(from, to);
      }
    },
    [columns, selectable, setColumns, onColumnDragEnd]
  );

  const handleResize = useCallback(
    (index) =>
      (e: SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
        e.stopPropagation();
        e.preventDefault();
        if ((e.target as HTMLElement).getAttribute('draggable')) {
          (e.target as HTMLElement).removeAttribute('draggable');
        }

        let width = data?.size?.width;
        const currentColumn = columns[index] as ITableColumn<RecordType>;
        const minWidth =
          Number(currentColumn?.minWidth) || DEFAULT_COLUMN_MIN_WIDTH;
        const maxWidth =
          Number(currentColumn?.maxWidth) || DEFAULT_COLUMN_MAX_WIDTH;
        if (width < minWidth) {
          width = minWidth;
        }
        if (width > maxWidth) {
          width = maxWidth;
        }

        const cols = [...columns];
        cols[index] = {
          ...cols[index],
          width,
        };
        setColumns(cols);

        if (onColumnResize) {
          onColumnResize(e, data);
        }
      },
    [onColumnResize, columns]
  );

  const handleSelectedRowChange = useCallback(
    (selectedRowKeys: React.Key[], selectedRows: RecordType[]) => {
      const updatedSelection = {
        rowKeys: selectedRowKeys,
        rows: selectedRows,
      };
      setSelected(selectedRowKeys);

      if (onRowSelectChange) {
        onRowSelectChange(updatedSelection);
      }
    },
    [setSelected, onRowSelectChange]
  );

  return (
    <div className={styles.DraggableTable}>
      <ReactDragListView.DragColumn
        onDragEnd={handleColumnDragEnd}
        nodeSelector="th"
        lineClassName="global-drag-line"
      >
        {selectable && (
          <AntTable
            components={{ header: { cell: ResizableColumn } }}
            columns={columns.map((column, index) => ({
              ...column,
              onHeaderCell: (col: any) => ({
                width: col.width,
                onResize: handleResize(index),
              }),
            }))}
            rowSelection={{
              selectedRowKeys: selected,
              onChange: handleSelectedRowChange,
            }}
            scroll={scrollVal}
            {...restProps}
          />
        )}
        {!selectable && (
          <AntTable
            components={{ header: { cell: ResizableColumn } }}
            columns={columns.map((column, index) => ({
              ...column,
              onHeaderCell: (col: any) => ({
                width: col.width,
                onResize: handleResize(index),
              }),
            }))}
            scroll={scrollVal}
            {...restProps}
          />
        )}
      </ReactDragListView.DragColumn>
    </div>
  );
};

export { DraggableTable };
